import { useEffect, useRef, useState } from "react";
import { requestExternalMediaReview, readExternalMediaReview, prepareExternalReviewEvidence, reconcileExternalReviewFailure } from "../../lib/externalMediaUpload";

export default function LearningVideoReview({ mediaId, filename, savedReviewId = null }) {
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [evidence, setEvidence] = useState(null);
  const [creditResolution, setCreditResolution] = useState(null);
  const locked = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    generation.current += 1;
    setReview(savedReviewId ? { review_id: savedReviewId, state: "unknown" } : null); setEvidence(null); setCreditResolution(null); setError(""); setBusy(false); locked.current = false;
    return () => { generation.current += 1; };
  }, [mediaId, savedReviewId]);
  async function act(kind) {
    if (locked.current) return;
    if (kind === "reconcile" && review?.state !== "failed") return;
    locked.current = true; setBusy(true); setError("");
    const version = generation.current;
    try {
      const data = kind === "reconcile"
        ? await reconcileExternalReviewFailure(mediaId, review.review_id)
        : kind === "prepare"
        ? await prepareExternalReviewEvidence(mediaId, review.review_id)
        : review ? await readExternalMediaReview(mediaId, review.review_id)
        : await requestExternalMediaReview(mediaId);
      if (version !== generation.current) return;
      if (kind === "reconcile") {
        if (data.media_id !== mediaId || data.review_id !== review.review_id || data.state !== "failed"
          || !data.credit_resolution_id || data.analysis_restarted !== false
          || !((data.credit_resolution === "returned" && data.returned_units === 1)
            || (data.credit_resolution === "not_charged" && data.returned_units === 0))) throw new Error("Invalid credit resolution receipt");
        setCreditResolution(data);
      } else if (kind === "prepare") {
        if (data.media_id !== mediaId || data.review_id !== review.review_id || !data.evidence_id
          || !["awaiting_intake", "intake_complete"].includes(data.status)) throw new Error("Invalid evidence receipt");
        setEvidence(data.evidence_id);
      } else {
        if (data.ndg_media_id !== mediaId || !data.review_id || (review && data.review_id !== review.review_id)
          || !["pending", "running", "completed", "failed"].includes(data.state)) throw new Error("Invalid review receipt");
        setReview(data);
      }
    } catch (err) {
      if (version === generation.current) setError(err?.response?.data?.detail?.code === "review_activation_required"
        ? "Video analysis is not enabled here yet. Your uploaded recording remains saved."
        : kind === "reconcile"
        ? err?.response?.data?.detail?.code === "failure_reconciliation_activation_required"
          ? "Credit recovery is not enabled here yet. Your recording is saved; no credit return has been confirmed."
          : "We couldn't confirm a credit return. Your recording is saved. Check this same analysis again; it will not restart analysis."
        : err?.response?.data?.detail?.code === "proof_request_already_returned"
        ? "A credit has already been returned for this failed analysis. Open your saved video reviews to check its status."
        : err?.response?.data?.detail?.code === "no_proof_credits"
        ? "You've used your included proof analyses. Your upload is saved. Add proof credits to continue analysis."
        : err?.response?.data?.detail?.code === "proof_accounting_unavailable"
        ? "Your upload is saved. We couldn't confirm your video analysis allowance. Retry this recording."
        : kind === "prepare"
        ? "This review could not be prepared for instructor checking. It may be incomplete or unavailable. No learning progress has been confirmed."
        : "We couldn't confirm the review request. The video may still be preparing. Retry to check the same recording.");
    } finally {
      if (version === generation.current) { locked.current = false; setBusy(false); }
    }
  }
  return <section className="my-3 rounded-xl border p-4" aria-label={`Learning review for ${filename}`}>
    <p>{filename}</p>
    <p>Video analysis is advisory. Your instructor must check the evidence with you.</p>
    {error && <p role="alert">{error}</p>}
    {evidence ? <p role="status">This video review is saved for instructor checking. It has not confirmed practical competence or assigned homework.</p> : <>
      {review?.state === "failed" ? <>
        <p role="status">Analysis failed. Your recording is saved, but it has not produced usable learning evidence.</p>
        {creditResolution ? <p role="status">{creditResolution.returned_units === 1
          ? "One proof credit has been returned. This failed analysis has not restarted."
          : "No proof credit was charged for this analysis. It has not restarted."}</p>
          : <button type="button" disabled={busy} onClick={() => act("reconcile")} className="block min-h-11 underline">
            {busy ? "Checking…" : "Check and recover analysis credit"}
          </button>}
      </> : <>
        {review && <p role="status">{review.state === "unknown" ? "A review request is saved. Check its current status." : review.state === "completed" ? "Analysis finished. Its suitability still needs checking." : "Analysis is waiting or running. Homework is not ready yet."}</p>}
        <button type="button" disabled={busy} onClick={() => act("check")} className="min-h-11 underline">
          {busy ? "Checking…" : review ? "Check analysis status" : "Request video analysis"}
        </button>
        {review?.state === "completed" && <button type="button" disabled={busy} onClick={() => act("prepare")} className="block min-h-11 underline">Prepare for instructor checking</button>}
      </>}
    </>}
  </section>;
}

